import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';


// MUI Components & Icons
import {
  ThemeProvider,
  createTheme,
  CssBaseline,
  Box,
  Typography,
  Grid,
  Paper,
  Avatar,
  Container,
  IconButton,
  BottomNavigation,
  BottomNavigationAction,
  CircularProgress,
  Stack,
  Alert,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Button
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  Assignment as AssignmentIcon,
  School as SchoolIcon,
  LiveTv as LiveTvIcon,
  Refresh as RefreshIcon,
  MenuBook as MenuBookIcon,
  ChevronLeft,
  ChevronRight,
  PlayArrow as JoinIcon
} from '@mui/icons-material';

// Framer Motion for animations
import { motion, AnimatePresence } from 'framer-motion';

// Hooks and Utilities
import { useAuth } from '../../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import { getCurrentLanguage } from '../../i18n'; // adjust this import path as needed
import useMediaQuery from '@mui/material/useMediaQuery';

// API functions (replace with your real API endpoints)
import { getUpcomingAssignments } from '../../api/assignments';
import { getDashboardStats, getRecentActivities } from '../../api/users';
import { getAllTeachers, getTeacherCalendarEvents, getTeacherCalendarNotes } from '../../api/teacher';
import { getStudentBookings } from '../../api/booking';

const fontFamily = 'inherit';
const theme = createTheme({});

// =============================================================================
// Helper Functions for Calendar
// =============================================================================
function getWeekStart(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // adjust for Sunday
  return new Date(d.setDate(diff));
}

function isToday(date) {
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
}

// =============================================================================
// Weekly Schedule Calendar (student's real bookings)
// =============================================================================
const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const DAY_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const toLocalDateStr = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

function getMondayOfWeek(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

const STATUS_STYLE = {
  confirmed: { bg: '#E8F5E9', border: '#A5D6A7', text: '#2E7D32' },
  pending:   { bg: '#FFF8EC', border: '#F5A623', text: '#E65100' },
  completed: { bg: '#E3F2FD', border: '#90CAF9', text: '#1565C0' },
  cancelled: { bg: '#FAFAFA', border: '#e0e0e0', text: '#9e9e9e' },
};

const WeeklyScheduleCalendar = ({ userId, navigate, lang }) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [weekStart, setWeekStart] = useState(getMondayOfWeek(new Date()));

  useEffect(() => {
    if (!userId) return;
    getStudentBookings(userId)
      .then(data => setBookings(data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [userId]);

  const weekDates = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + i);
    return d;
  });

  const weekDateStrs = weekDates.map(d => toLocalDateStr(d));

  // Only show time rows that have at least one booking this week
  const weekBookings = bookings.filter(b => weekDateStrs.includes(b.date));
  const timeSlots = [...new Set(weekBookings.map(b => b.startTime))].sort();

  const getBooking = (dateStr, time) =>
    weekBookings.find(b => b.date === dateStr && b.startTime === time) || null;

  const navigateWeek = (dir) => {
    setWeekStart(prev => {
      const d = new Date(prev);
      d.setDate(d.getDate() + dir * 7);
      return d;
    });
  };

  const today = toLocalDateStr(new Date());

  return (
    <Paper elevation={0} sx={{ border: '1px solid #eee', borderRadius: 3, overflow: 'hidden', mb: 3 }}>
      {/* Header */}
      <Box sx={{ px: 3, pt: 2.5, pb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" fontWeight={700} color="#1a1a1a">
          My Weekly Schedule
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton size="small" onClick={() => navigateWeek(-1)} sx={{ color: '#666' }}>
            <ChevronLeft />
          </IconButton>
          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 130, textAlign: 'center' }}>
            {weekDates[0].toLocaleDateString('en', { month: 'short', day: 'numeric' })}
            {' – '}
            {weekDates[6].toLocaleDateString('en', { month: 'short', day: 'numeric', year: 'numeric' })}
          </Typography>
          <IconButton size="small" onClick={() => navigateWeek(1)} sx={{ color: '#666' }}>
            <ChevronRight />
          </IconButton>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <CircularProgress size={28} sx={{ color: '#F5A623' }} />
        </Box>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 700, tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow sx={{ bgcolor: '#FAFAFA' }}>
                {/* Time header */}
                <TableCell
                  sx={{ width: 72, fontWeight: 700, fontSize: '0.78rem', color: '#F5A623',
                    borderBottom: '2px solid #f0f0f0', py: 1.5, pl: 2 }}
                >
                  Time
                </TableCell>
                {weekDates.map((d, i) => {
                  const isToday = toLocalDateStr(d) === today;
                  const dateLabel = d.toLocaleDateString('en', { day: 'numeric', month: 'short' });
                  return (
                    <TableCell key={i} align="center"
                      sx={{ fontWeight: 700, fontSize: '0.82rem',
                        color: isToday ? '#F5A623' : '#333',
                        borderBottom: '2px solid #f0f0f0', py: 1.5 }}>
                      {DAY_SHORT[i]}
                      <Box component="span" sx={{
                        display: 'block', fontSize: '0.7rem', fontWeight: 400,
                        color: isToday ? '#F5A623' : '#888'
                      }}>
                        {dateLabel}
                      </Box>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {timeSlots.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center"
                    sx={{ py: 5, color: '#bbb', fontSize: '0.9rem', border: 0 }}>
                    No lessons scheduled this week
                  </TableCell>
                </TableRow>
              ) : (
                timeSlots.map(time => (
                  <TableRow key={time} sx={{ '&:last-child td': { border: 0 } }}>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#F5A623',
                      borderBottom: '1px solid #f5f5f5', pl: 2, verticalAlign: 'top', pt: 1.5 }}>
                      {time}
                    </TableCell>
                    {weekDateStrs.map((dateStr, di) => {
                      const b = getBooking(dateStr, time);
                      const style = STATUS_STYLE[b?.status] || STATUS_STYLE.cancelled;
                      return (
                        <TableCell key={di} align="center"
                          sx={{ borderBottom: '1px solid #f5f5f5', p: 0.8, verticalAlign: 'top' }}>
                          {b && (
                            <Box sx={{
                              bgcolor: style.bg,
                              border: `1.5px solid ${style.border}`,
                              borderRadius: 1.5, p: 1, textAlign: 'left'
                            }}>
                              <Typography variant="caption" fontWeight={700}
                                sx={{ color: style.text, display: 'block', lineHeight: 1.3 }}>
                                {b.teacherName}
                              </Typography>
                              <Typography variant="caption"
                                sx={{ color: style.text, opacity: 0.85, display: 'block' }}>
                                {b.startTime} - {b.endTime}
                              </Typography>
                              {b.status === 'confirmed' && b.zoomLink && (
                                <Button size="small" startIcon={<JoinIcon sx={{ fontSize: 12 }} />}
                                  onClick={() => window.open(b.zoomLink, '_blank')}
                                  sx={{
                                    mt: 0.5, py: 0, px: 0.5, minWidth: 0,
                                    fontSize: '0.68rem', fontWeight: 700, color: '#2E7D32',
                                    textTransform: 'none',
                                    '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' }
                                  }}>
                                  Join
                                </Button>
                              )}
                            </Box>
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* Legend */}
      <Box sx={{ display: 'flex', gap: 2, px: 3, py: 1.5, borderTop: '1px solid #f5f5f5' }}>
        {[
          { label: 'Confirmed', style: STATUS_STYLE.confirmed },
          { label: 'Pending',   style: STATUS_STYLE.pending   },
          { label: 'Completed', style: STATUS_STYLE.completed },
        ].map(({ label, style }) => (
          <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
            <Box sx={{ width: 11, height: 11, borderRadius: '50%',
              bgcolor: style.bg, border: `2px solid ${style.border}` }} />
            <Typography variant="caption" sx={{ color: style.text, fontWeight: 600 }}>
              {label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

// =============================================================================
// CalendarCard Component
// (Read‑only view; students can only view the teacher's schedule.)
// =============================================================================
const CalendarCard = ({ schedule }) => {
  const { t } = useTranslation();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentWeekStart, setCurrentWeekStart] = useState(getWeekStart(new Date()));

  const hasEvents = (date) => {
    if (!schedule || !Array.isArray(schedule)) return false;
    const dateStr = toLocalDateStr(date);
    return schedule.some((event) => event.date === dateStr);
  };

  const weekDates = Array.from({ length: 7 }, (_, i) => {
    const date = new Date(currentWeekStart);
    date.setDate(currentWeekStart.getDate() + i);
    return date;
  });

  const selectedDateStr = toLocalDateStr(selectedDate);
  const eventsForDay = schedule ? schedule.filter(event => event.date === selectedDateStr) : [];

  return (
    <Box sx={{ height: '100%', p: 2 }}>
      {/* Calendar Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3
        }}
      >
        <Typography variant="h6" sx={{ color: '#1a1a1a', fontWeight: 600 }}>
          {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton
            onClick={() => {
              const newWeekStart = new Date(currentWeekStart);
              newWeekStart.setDate(currentWeekStart.getDate() - 7);
              setCurrentWeekStart(newWeekStart);
            }}
            sx={{
              color: '#1a1a1a',
              backgroundColor: 'rgba(245, 166, 35, 0.1)',
              '&:hover': { backgroundColor: 'rgba(245, 166, 35, 0.2)' }
            }}
          >
            <CalendarIcon sx={{ transform: 'rotate(-90deg)' }} />
          </IconButton>
          <IconButton
            onClick={() => {
              const newWeekStart = new Date(currentWeekStart);
              newWeekStart.setDate(currentWeekStart.getDate() + 7);
              setCurrentWeekStart(newWeekStart);
            }}
            sx={{
              color: '#1a1a1a',
              backgroundColor: 'rgba(245, 166, 35, 0.1)',
              '&:hover': { backgroundColor: 'rgba(245, 166, 35, 0.2)' }
            }}
          >
            <CalendarIcon sx={{ transform: 'rotate(90deg)' }} />
          </IconButton>
        </Box>
      </Box>

      {/* Calendar Days */}
      <Grid container spacing={1}>
        {weekDates.map((date, index) => {
          const isCurrentDay = isToday(date);
          const isSelectedDay =
            date.getDate() === selectedDate.getDate() &&
            date.getMonth() === selectedDate.getMonth() &&
            date.getFullYear() === selectedDate.getFullYear();
          const dayHasEvents = hasEvents(date);

          return (
            <Grid item xs key={index}>
              <Paper
                component={motion.div}
                whileTap={{ scale: 0.95 }}
                onClick={() => setSelectedDate(new Date(date))}
                sx={{
                  p: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  cursor: 'pointer',
                  backgroundColor: isSelectedDay
                    ? 'rgba(245, 166, 35, 0.1)'
                    : '#f5f5f5',
                  border: isSelectedDay
                    ? '1px solid #F5A623'
                    : '1px solid #eee',
                  borderRadius: '12px',
                  position: 'relative',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(245, 166, 35, 0.05)',
                    border: '1px solid rgba(245, 166, 35, 0.5)'
                  }
                }}
              >
                <Typography
                  sx={{
                    fontSize: '1rem',
                    fontWeight: isCurrentDay || isSelectedDay ? 600 : 400,
                    color: isCurrentDay ? '#F5A623' : '#fff',
                    mb: 0.5
                  }}
                >
                  {date.getDate()}
                </Typography>
                {dayHasEvents && (
                  <Box
                    sx={{
                      width: 4,
                      height: 4,
                      borderRadius: '50%',
                      backgroundColor: '#F5A623',
                      position: 'absolute',
                      bottom: 4
                    }}
                  />
                )}
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      {/* Events List */}
      <Box sx={{ mt: 3 }}>
        <Typography
          variant="subtitle2"
          sx={{
            color: '#333',
            fontWeight: 500,
            mb: 2
          }}
        >
          {selectedDate.toLocaleDateString('en-US', {
            weekday: 'long',
            day: 'numeric',
            month: 'long'
          })}
        </Typography>
        <Stack spacing={2}>
          {eventsForDay.map((event, index) => (
            <Paper
              key={index}
              elevation={0}
              sx={{
                p: 2,
                backgroundColor: '#f5f5f5',
                borderRadius: '12px',
                border: '1px solid #eee'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: '#F5A623'
                  }}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      color: '#1a1a1a',
                      fontWeight: 500,
                      mb: 0.5
                    }}
                  >
                    {event.title}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: '#666' }}
                  >
                    {event.time}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          ))}
          {eventsForDay.length === 0 && (
            <Box
              sx={{
                textAlign: 'center',
                py: 3,
                color: '#666',
                backgroundColor: '#f5f5f5',
                borderRadius: '12px',
                border: '1px solid #eee'
              }}
            >
              <Typography variant="body2">{t('calendar.noEvents')}</Typography>
            </Box>
          )}
        </Stack>
      </Box>
    </Box>
  );
};

// =============================================================================
// StudentDashboard Component
// =============================================================================
const StudentDashboard = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const currentLang = getCurrentLanguage();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // State declarations for real data
  const [upcomingAssignments, setUpcomingAssignments] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [calendarData, setCalendarData] = useState({ events: [], notes: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  // Bottom navigation: 0 = Schedule, 1 = Assignments, 2 = Teachers
  const [currentTab, setCurrentTab] = useState(0);
  // When a teacher is selected, we show their calendar in the Schedule tab.
  const [selectedTeacher, setSelectedTeacher] = useState(null);

  // Fetch dashboard data from real API endpoints
  const fetchDashboardData = async () => {
    if (!user?.id) return;
    try {
      const assignments = await getUpcomingAssignments(user.id);
      const teachersList = await getAllTeachers();
      // Optionally, you can also fetch dashboard stats if needed:
      await getDashboardStats(user.id);
      // Recent activities API call is removed since Activity tab is not used.
      setUpcomingAssignments(assignments || []);
      setTeachers(teachersList || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError(t('errors.dashboardFetch'));
    } finally {
      setLoading(false);
    }
  };

  // When a teacher is selected, fetch that teacher's calendar data.
  useEffect(() => {
    const fetchTeacherCalendar = async () => {
      if (selectedTeacher) {
        const now = new Date();
        const startOfWeek = getWeekStart(now);
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        try {
          const events = await getTeacherCalendarEvents(
            selectedTeacher.id,
            startOfWeek,
            endOfWeek
          );
          const notes = await getTeacherCalendarNotes(
            selectedTeacher.id,
            startOfWeek,
            endOfWeek
          );
          setCalendarData({ events: events || [], notes: notes || {} });
        } catch (err) {
          console.error('Error fetching teacher calendar:', err);
          setError(t('errors.calendarFetch'));
        }
      }
    };
    fetchTeacherCalendar();
  }, [selectedTeacher, t]);

  // Refresh handler calls dashboard data and teacher calendar (if needed)
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchDashboardData();
      if (selectedTeacher) {
        const now = new Date();
        const startOfWeek = getWeekStart(now);
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        const events = await getTeacherCalendarEvents(
          selectedTeacher.id,
          startOfWeek,
          endOfWeek
        );
        const notes = await getTeacherCalendarNotes(
          selectedTeacher.id,
          startOfWeek,
          endOfWeek
        );
        setCalendarData({ events: events || [], notes: notes || {} });
      }
    } catch (err) {
      setError(t('errors.refreshFailed'));
    } finally {
      setRefreshing(false);
    }
  };

  // Initial load
  useEffect(() => {
    if (user) {
      fetchDashboardData();
    }
  }, [user, t]);

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(145deg, #FAFAFA 0%, #FAFAFA 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <CircularProgress sx={{ color: '#F5A623' }} />
      </Box>
    );
  }

  return (
        <Box
          sx={{
            minHeight: '100vh',
            background: 'linear-gradient(145deg, #FAFAFA 0%, #FAFAFA 100%)',
            display: 'flex',
            flexDirection: 'column',
            pb: 7,
            pt: { xs: 12, sm: 14 }
          }}
        >
          <Container maxWidth="xl" sx={{ flex: 1, px: 2, py: 3, overflowY: 'auto' }}>
            {/* Header with Welcome & Refresh */}
            <Box
              sx={{
                mb: 4,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <Typography
                variant="h5"
                component={motion.h1}
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                sx={{ color: '#1a1a1a', fontWeight: 600, letterSpacing: '0.5px' }}
              >
                {t('dashboard.welcome', { name: user?.displayName || t('common.student') })}
              </Typography>
              <Tooltip title={t('common.refresh')}>
                <IconButton onClick={handleRefresh} disabled={refreshing} sx={{ color: '#F5A623' }}>
                  <RefreshIcon
                    sx={{
                      animation: refreshing ? 'spin 1s linear infinite' : 'none'
                    }}
                  />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Quick Actions (Mobile Cards) */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {[
                {
                  icon: <LiveTvIcon sx={{ fontSize: 28, color: '#F5A623' }} />,
                  text: t('dashboard.quickActions.browseLiveClasses'),
                  action: () => navigate(`/${currentLang}/student/live-classes`)
                },
                {
                  icon: <AssignmentIcon sx={{ fontSize: 28, color: '#F5A623' }} />,
                  text: t('dashboard.quickActions.viewAssignments'),
                  action: () => setCurrentTab(1)
                }
              ].map((action, index) => (
                <Grid item xs={6} key={index}>
                  <Paper
                    component={motion.div}
                    whileTap={{ scale: 0.95 }}
                    elevation={0}
                    onClick={action.action}
                    sx={{
                      p: 2,
                      height: '100%',
                      borderRadius: '12px',
                      bgcolor: '#fff',
                      backdropFilter: 'blur(10px)',
                      border: action.text === t('dashboard.quickActions.browseLiveClasses') 
                        ? '2px solid #F5A623'
                        : '1px solid #eee',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 1,
                      textAlign: 'center',
                      animation: action.text === t('dashboard.quickActions.browseLiveClasses')
                        ? 'pulsingOutline 1.5s ease-in-out infinite'
                        : 'none',
                      '@keyframes pulsingOutline': {
                        '0%': {
                          boxShadow: '0 0 0 0 rgba(245, 166, 35, 0.7)',
                        },
                        '50%': {
                          boxShadow: '0 0 0 8px rgba(245, 166, 35, 0)',
                        },
                        '100%': {
                          boxShadow: '0 0 0 0 rgba(245, 166, 35, 0)',
                        },
                      },
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: 'rgba(245, 166, 35, 0.1)',
                        width: 44,
                        height: 44,
                        mb: 1
                      }}
                    >
                      {action.icon}
                    </Avatar>
                    <Typography variant="body2" sx={{ color: '#1a1a1a', fontWeight: 500, fontSize: '0.875rem' }}>
                      {action.text}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            {/* Weekly Schedule Calendar */}
            <WeeklyScheduleCalendar
              userId={user?.id}
              navigate={navigate}
              lang={currentLang}
            />

            {/* Tab Content */}
            <Box sx={{ display: currentTab === 0 ? 'block' : 'none' }}>
              {/* Schedule Tab */}
              {selectedTeacher && (
                <CalendarCard schedule={calendarData.events} />
              )}
            </Box>

            <Box sx={{ display: currentTab === 1 ? 'block' : 'none' }}>
              {/* Assignments Tab */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: '#1a1a1a',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <AssignmentIcon sx={{ color: '#F5A623' }} />
                  {t('dashboard.assignments.title')}
                </Typography>
              </Box>
              <Stack spacing={2}>
                {upcomingAssignments.map((assignment) => (
                  <Paper
                    key={assignment.id}
                    elevation={0}
                    onClick={() =>
                      navigate(`/${currentLang}/student/assignments/${assignment.id}`)
                    }
                    sx={{
                      p: 2,
                      borderRadius: '12px',
                      bgcolor: '#fff',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid #eee',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:active': { transform: 'scale(0.98)' }
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ color: '#1a1a1a', fontWeight: 500, mb: 1 }}>
                      {assignment.title}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: '#666', display: 'block', mb: 1 }}
                    >
                      {assignment.dueDate
                        ? new Date(assignment.dueDate?.toDate ? assignment.dueDate.toDate() : assignment.dueDate).toLocaleDateString('en', {
                            weekday: 'long', month: 'long', day: 'numeric'
                          })
                        : 'No due date'}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress
                        variant="determinate"
                        value={assignment.progress || 0}
                        size={24}
                        thickness={4}
                        sx={{ color: '#F5A623' }}
                      />
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        {assignment.progress || 0}% {t('dashboard.assignments.complete')}
                      </Typography>
                    </Box>
                  </Paper>
                ))}
                {upcomingAssignments.length === 0 && (
                  <Box
                    sx={{
                      textAlign: 'center',
                      py: 4,
                      color: '#666',
                      bgcolor: '#f5f5f5',
                      borderRadius: '12px',
                      border: '1px solid #eee'
                    }}
                  >
                    <MenuBookIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5, color: '#F5A623' }} />
                    <Typography variant="body2">
                      {t('dashboard.assignments.noAssignments')}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Box>

            <Box sx={{ display: currentTab === 2 ? 'block' : 'none' }}>
              {/* Teachers Tab */}
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    color: '#1a1a1a',
                    fontWeight: 600,
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <SchoolIcon sx={{ color: '#F5A623' }} />
                  {t('dashboard.teachers.title')}
                </Typography>
                <Grid container spacing={2}>
                  {teachers.map((teacher) => (
                    <Grid item xs={12} sm={6} key={teacher.id}>
                      <Paper
                        component={motion.div}
                        whileTap={{ scale: 0.95 }}
                        elevation={0}
                        onClick={() => {
                          setSelectedTeacher(teacher);
                          setCurrentTab(0); // switch to Schedule tab upon selection
                        }}
                        sx={{
                          p: 2,
                          borderRadius: '12px',
                          bgcolor:
                            selectedTeacher?.id === teacher.id
                              ? 'rgba(245, 166, 35, 0.1)'
                              : '#fff',
                          backdropFilter: 'blur(10px)',
                          border: '1px solid',
                          borderColor:
                            selectedTeacher?.id === teacher.id
                              ? '#F5A623'
                              : '#eee',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar
                            sx={{
                              width: 48,
                              height: 48,
                              bgcolor: 'rgba(245, 166, 35, 0.1)',
                              color: '#F5A623'
                            }}
                          >
                            {teacher.displayName?.[0] || '?'}
                          </Avatar>
                          <Box sx={{ flex: 1 }}>
                            <Typography sx={{ color: '#1a1a1a', fontWeight: 500 }}>
                              {teacher.displayName || t('common.unknownTeacher')}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#666' }}>
                              {teacher.email}
                            </Typography>
                          </Box>
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                  {teachers.length === 0 && (
                    <Box
                      sx={{
                        textAlign: 'center',
                        py: 4,
                        color: '#666',
                        bgcolor: '#f5f5f5',
                        borderRadius: '12px',
                        border: '1px solid #eee'
                      }}
                    >
                      <SchoolIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5, color: '#F5A623' }} />
                      <Typography variant="body2">{t('dashboard.teachers.empty')}</Typography>
                    </Box>
                  )}
                </Grid>
              </Box>
            </Box>

          </Container>

          {/* Bottom Navigation */}
          <Paper
            sx={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: '#fff',
              backdropFilter: 'blur(10px)',
              borderTop: '1px solid #eee',
              zIndex: 1000
            }}
            elevation={0}
          >
            <BottomNavigation
              value={currentTab}
              onChange={(event, newValue) => setCurrentTab(newValue)}
              sx={{
                backgroundColor: 'transparent',
                height: 65,
                '& .MuiBottomNavigationAction-root': {
                  color: '#999',
                  '&.Mui-selected': { color: '#F5A623' },
                  '& .MuiBottomNavigationAction-label': { fontSize: '0.625rem' }
                }
              }}
            >
              <BottomNavigationAction label={t('nav.schedule')} icon={<CalendarIcon />} />
              <BottomNavigationAction label={t('nav.assignments')} icon={<AssignmentIcon />} />
              <BottomNavigationAction label={t('nav.teachers')} icon={<SchoolIcon />} />
            </BottomNavigation>
          </Paper>
        </Box>
  );
};

export default StudentDashboard;
